import {
  Box,
  Divider,
  MenuItem,
  Stack,
  type StackProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { ObjectLiteral } from 'api/submission';
import warningIcon from 'assets/icons/warning.svg';
import {
  APPLICATION_STEPS,
  BINARY_CONSTANTS,
  genericErrorMessage,
  INDIVIDUAL_OWNDERS_PAGE_DESC,
} from 'common/constants';
import { ExposuresWrapper } from 'components/ExposuresWrapper';
import { FormControl } from 'components/lib/FormControl';
import { NumberFormatWithWrapper } from 'components/lib/NumberFormat';
import type { Field } from 'components/lib/QuestionEngine';
import { Select } from 'components/lib/Select';
import { ToggleButtonsGroup } from 'components/lib/ToggleButtonsGroup';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { displayToastMessage } from 'components/ToastMessage';
import { useFormik } from 'formik';
import { useBulkExposuresOrGroupFields } from 'hooks/useBulkExposuresOrGroupFields';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { useManualPremiumPerils } from 'hooks/useManualPremiumPerils';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { isEmpty, isEqual, omit, pick } from 'lodash-es';
import { type FC, useMemo } from 'react';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type { IOwner } from '../Owners/Owners.types';
import { IndividualOwnersWorkerCompensationWarningMessageWrapper } from './IndividualOwners.styles';
import { getWcOwnerStateRule } from './wcOwnerStateRules';

const NJ_LLC_WC_MIXED_STATE_ERROR_MESSAGE =
  "All LLC members must be either all included or all excluded from Workers' Compensation.";

const { fields: initialFields } = APPLICATION_STEPS.individual_owners;

const fields = initialFields.reduce(
  (acc, field) => {
    acc[field.code] = field;
    return acc;
  },
  {} as {
    [key: (typeof initialFields)[number]['code']]: Field;
  },
);

const WorkersCompensationWarningBanner: FC<StackProps> = ({ children, sx, ...rest }) => (
  <Stack
    {...rest}
    sx={[
      IndividualOwnersWorkerCompensationWarningMessageWrapper,
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <SvgIconWrapper src={warningIcon} className="workers-compensation-warning-icon" />
    <Typography variant="body2" fontWeight={700}>
      {children}
    </Typography>
  </Stack>
);

const IndividualOwners: FC = () => {
  const theme = useTheme();

  const {
    submissionId,
    relatedResource: owners,
    submission,
  } = useExposuresOrGroupFields<IOwner>({
    type: 'group',
    name: 'pol_oo',
  });

  const pageDescritpion =
    INDIVIDUAL_OWNDERS_PAGE_DESC[submission?.data?.uw_questions?.entityType] ||
    INDIVIDUAL_OWNDERS_PAGE_DESC.LLC;

  const { bulkUpdateGroupField } = useBulkExposuresOrGroupFields(['bulkUpdateGroupField']);

  const { setFullscreenLoading } = useFullscreenLoader();

  const { manualPremiumPerils, mainPeril } = useManualPremiumPerils();

  const {
    isNjLlcAllInOrAllOut: isNjLlcWcAllInOrAllOutRuleActive,
    lockedToggleOption: lockedWcToggleOption,
    forcedWcExcludedValue,
    stateMessage: stateWcRuleMessage,
  } = getWcOwnerStateRule({
    usState: submission?.us_state,
    entityType: submission?.data?.uw_questions?.entityType,
  });

  const baseDescription = `Indicate the job duty and payroll of each ${pageDescritpion.descriprion}, or mark them as excluded from Workers' Compensation coverage.`;

  const validationSchema = yup.lazy(() =>
    yup.object().shape(
      owners?.reduce((acc, owner) => {
        acc[owner.id] = yup.object().shape({
          pol_oo_wc_excluded: yup.string().required(),
          pol_oo_wc_class_code: yup.string().when('pol_oo_wc_excluded', {
            is: (pol_oo_wc_excluded: string) => pol_oo_wc_excluded === BINARY_CONSTANTS.NO,
            then: () => yup.string().required(),
            otherwise: () => yup.string().notRequired(),
          }),
          pol_oo_wc_payroll: yup.string().when('pol_oo_wc_excluded', {
            is: (pol_oo_wc_excluded: string) => pol_oo_wc_excluded === BINARY_CONSTANTS.NO,
            then: () => yup.string().required(),
            otherwise: () => yup.string().notRequired(),
          }),
        });
        return acc;
      }, {}),
    ),
  );

  const formik = useFormik({
    initialValues: owners?.reduce(
      (acc, owner) => {
        const ownerFields = pick(owner.data, [
          'pol_oo_wc_class_code',
          'pol_oo_wc_excluded',
          'pol_oo_wc_payroll',
        ]);

        acc[owner.id] = forcedWcExcludedValue
          ? { ...ownerFields, pol_oo_wc_excluded: forcedWcExcludedValue }
          : ownerFields;
        return acc;
      },
      {} as {
        [key: string]: Pick<
          IOwner,
          'pol_oo_wc_class_code' | 'pol_oo_wc_excluded' | 'pol_oo_wc_payroll'
        >;
      },
    ),
    onSubmit: () => {},
    validationSchema,
  });

  const validateFormik = async () => {
    await formik.submitForm();
    const errors = await formik.validateForm();
    if (!isEmpty(errors)) {
      await formik.setTouched(
        {
          ...formik.touched,
          ...owners.reduce((acc, owner) => {
            acc[owner.id] = {
              pol_oo_wc_class_code: true,
              pol_oo_wc_excluded: true,
              pol_oo_wc_payroll: true,
            };
            return acc;
          }, {}),
        },
        false,
      );

      return false;
    }

    return true;
  };

  const handleNext = async () => {
    const isValid = await validateFormik();

    if (!isValid) {
      return false;
    }

    if (isNjLlcWcAllInOrAllOutRuleActive) {
      const distinctExclusionValues = new Set(
        Object.values(formik.values).map((owner) => owner.pol_oo_wc_excluded),
      );

      if (distinctExclusionValues.size > 1) {
        displayToastMessage('ERROR', NJ_LLC_WC_MIXED_STATE_ERROR_MESSAGE);
        return false;
      }
    }

    try {
      setFullscreenLoading(true, 'Updating individual owners');

      const ownersUpdateArray: ObjectLiteral[] = [];

      Object.entries(formik.values).forEach(([key, value]) => {
        const oldData = owners.find((owner) => owner.id === key);
        const newData = {
          ...oldData?.data,
          ...value,
        };

        if (!isEqual(oldData?.data, newData)) {
          const updateOwner = {
            name: 'pol_oo',
            id: key,
            data:
              newData.pol_oo_wc_excluded === BINARY_CONSTANTS.YES
                ? omit(newData, 'pol_oo_wc_payroll', 'pol_oo_wc_class_code')
                : newData,
          };

          ownersUpdateArray.push(updateOwner);
        }
      });

      if (!isEmpty(ownersUpdateArray)) {
        await bulkUpdateGroupField({
          submissionId,
          body: ownersUpdateArray,
        });
      }
    } catch (_err) {
      displayToastMessage('ERROR', genericErrorMessage);

      return false;
    } finally {
      setFullscreenLoading(false);
    }

    return true;
  };

  const renderWorkerCompensationWarningMessage = useMemo(
    () =>
      Object.values(formik?.values).every(
        (owner) => owner?.pol_oo_wc_excluded === BINARY_CONSTANTS.YES,
      ) && submission?.data?.uw_questions?.numEmployees === '0',
    [formik?.values, submission?.data?.uw_questions?.numEmployees],
  );

  useScrollToFirstErroredInput({ formik });

  return (
    <ExposuresWrapper
      title={pageDescritpion.title}
      description={baseDescription}
      renderAddButton={false}
      onNext={handleNext}
    >
      <Stack>
        {stateWcRuleMessage && (
          <WorkersCompensationWarningBanner sx={{ m: (theme) => theme.spacing(0, 0, 3) }}>
            {stateWcRuleMessage}
          </WorkersCompensationWarningBanner>
        )}

        <Stack gap={3} divider={<Divider />}>
          {owners?.map((owner) => {
            const formikOwner = formik.values[owner.id];

            const areFieldsDisabled = formikOwner.pol_oo_wc_excluded === BINARY_CONSTANTS.YES;

            const formikOwnerError = {
              pol_oo_wc_class_code: {
                error:
                  formik.touched[owner.id]?.pol_oo_wc_class_code &&
                  Boolean(formik.errors[owner.id]?.pol_oo_wc_class_code),
                errorMessage: formik.errors[owner.id]?.pol_oo_wc_class_code as string,
              },
              pol_oo_wc_payroll: {
                error:
                  formik.touched[owner.id]?.pol_oo_wc_payroll &&
                  Boolean(formik.errors[owner.id]?.pol_oo_wc_payroll),
                errorMessage: formik.errors[owner.id]?.pol_oo_wc_payroll as string,
              },
            };

            return (
              <Stack
                key={owner.id}
                sx={{
                  gap: '1rem',
                }}
              >
                <Typography variant="h2" sx={omit(theme.mixins.textOverflowStyle(), 'maxWidth')}>
                  {owner.data.pol_oo_name_first} {owner.data.pol_oo_name_last}
                </Typography>

                <FormControl>
                  <Typography variant="body1" mb={1.2}>
                    Exclude this individual from Workers Compensation coverage?
                  </Typography>

                  <Tooltip
                    title={
                      isNjLlcWcAllInOrAllOutRuleActive
                        ? formikOwner.pol_oo_wc_excluded === BINARY_CONSTANTS.YES
                          ? 'Including one member will include all other owners/officers.'
                          : 'Excluding one member will exclude all other owners/officers.'
                        : ''
                    }
                    placement="top"
                    arrow
                  >
                    <Box component="span">
                      <ToggleButtonsGroup
                        about=""
                        options={[BINARY_CONSTANTS.YES, BINARY_CONSTANTS.NO].map((option) => ({
                          value: option,
                          label: option,
                          disabled: lockedWcToggleOption?.value === option,
                          tooltip:
                            lockedWcToggleOption?.value === option
                              ? lockedWcToggleOption.tooltip
                              : undefined,
                        }))}
                        onChange={(_, newValue) => {
                          if (!newValue) return;

                          if (isNjLlcWcAllInOrAllOutRuleActive) {
                            formik.setTouched({}, false);
                            formik.setValues(
                              Object.fromEntries(
                                Object.keys(formik.values).map((ownerId) => [
                                  ownerId,
                                  {
                                    pol_oo_wc_class_code:
                                      newValue === BINARY_CONSTANTS.NO
                                        ? mainPeril?.data?.man_class_code || ''
                                        : '',
                                    pol_oo_wc_payroll: '',
                                    pol_oo_wc_excluded: newValue,
                                  },
                                ]),
                              ),
                            );
                            return;
                          }

                          formik.setFieldTouched(owner.id, false);
                          formik.setValues({
                            ...formik.values,
                            [owner.id]: {
                              pol_oo_wc_class_code: '',
                              pol_oo_wc_payroll: '',
                              pol_oo_wc_excluded: newValue,
                            },
                          });
                        }}
                        value={formikOwner.pol_oo_wc_excluded}
                        inputVariant="radio"
                      />
                    </Box>
                  </Tooltip>
                </FormControl>

                <FormControl
                  renderLabel
                  field={{
                    ...fields.pol_oo_wc_class_code,
                    name: fields.pol_oo_wc_class_code.name,
                  }}
                  {...formikOwnerError[fields.pol_oo_wc_class_code.code]}
                  disabled={areFieldsDisabled}
                >
                  <Select
                    id={fields.pol_oo_wc_class_code.code}
                    name={fields.pol_oo_wc_class_code.code}
                    value={formikOwner.pol_oo_wc_class_code || ''}
                    isFieldOptional={false}
                    label={fields.pol_oo_wc_class_code.name}
                    labelId={`${fields.pol_oo_wc_class_code.code}_label`}
                    onChange={(e) => {
                      formik.setFieldValue(
                        `${owner.id}.${fields.pol_oo_wc_class_code.code}`,
                        e.target.value,
                      );
                    }}
                    error={formikOwnerError[fields.pol_oo_wc_class_code.code].error}
                    testPrefix={`q_${fields.pol_oo_wc_class_code.code}`}
                    inputProps={{
                      readOnly: areFieldsDisabled,
                    }}
                    optionsCount={manualPremiumPerils?.length}
                    preSelectFirstOption={() => {
                      const firstOption = manualPremiumPerils?.at(0);
                      formik.setFieldValue(
                        `${owner.id}.${fields.pol_oo_wc_class_code.code}`,
                        firstOption?.data?.man_class_code,
                      );
                    }}
                  >
                    {manualPremiumPerils?.map((manualPeril) => (
                      <MenuItem
                        key={manualPeril.data.man_class_code}
                        value={manualPeril.data.man_class_code}
                      >
                        {manualPeril.data.man_class_code_desc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <NumberFormatWithWrapper
                  disabled={areFieldsDisabled}
                  value={formikOwner.pol_oo_wc_payroll || ''}
                  name={fields.pol_oo_wc_payroll.code}
                  testPrefix={`q_${fields.pol_oo_wc_payroll.code}`}
                  field={fields.pol_oo_wc_payroll}
                  inputProps={{ prefix: '$' }}
                  error={formikOwnerError[fields.pol_oo_wc_payroll.code].error}
                  helperText={
                    formikOwnerError[fields.pol_oo_wc_payroll.code].error &&
                    formikOwnerError[fields.pol_oo_wc_payroll.code].errorMessage
                  }
                  onChange={(e) =>
                    formik.setFieldValue(
                      `${owner.id}.${fields.pol_oo_wc_payroll.code}`,
                      e.target.value,
                    )
                  }
                />
              </Stack>
            );
          })}
        </Stack>

        {renderWorkerCompensationWarningMessage && (
          <WorkersCompensationWarningBanner>
            Workers Compensation will be <Typography variant="specialInfo">EXCLUDED</Typography>{' '}
            from your quote because you opted to exclude all owners and reported no employees.
          </WorkersCompensationWarningBanner>
        )}
      </Stack>
    </ExposuresWrapper>
  );
};

export default IndividualOwners;
